import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ipApi } from '../../services/api';
import { Q, queueRowId, DEFAULT_QUEUE_TAGS } from '../../config/queueStatus';
import { useHospitalDirectory } from '../../hooks/useHospitalDirectory';
import { buildIpdAdmitLink } from '../../utils/opIpLink';

const TIME_SLOTS = [
  '09:00', '09:20', '09:40', '10:00', '10:20', '10:40', '11:00', '11:20', '11:40',
  '12:00', '16:00', '16:20', '16:40', '17:00', '17:20', '17:40', '18:00', '18:20',
];

export default function BookAppointmentForm({ user, patients, onDone, onCancel, defaultVisitType = 'New' }) {
  const { data: dir } = useHospitalDirectory();
  const doctors = dir?.doctors || [];
  const defaultFee = dir?.hospital?.pricing?.opConsultFee || 500;

  const [form, setForm] = useState({
    patientName: '', patientId: '', patientPhone: '', age: '', gender: 'M',
    doctors: doctors[0]?.name ? [doctors[0].name] : [],
    visitDate: new Date().toISOString().slice(0, 10),
    appointmentTime: '09:00', slotDuration: 20, teleconsult: false,
    visitType: defaultVisitType, fee: defaultFee, reason: '', tags: [],
  });
  const [patientPick, setPatientPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pickPatient = (patientId) => {
    const p = (patients || []).find((x) => x.patientId === patientId);
    if (!p) return;
    setForm((f) => ({
      ...f, patientName: p.name, patientId: p.patientId, patientPhone: p.phone || '',
      age: p.age || '', gender: p.gender || 'M',
    }));
    setPatientPick('');
  };

  const toggleTag = (tag) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter((t) => t !== tag) : [...f.tags, tag],
    }));
  };

  const toggleDoctor = (name) => {
    setForm((f) => ({
      ...f,
      doctors: f.doctors.includes(name) ? f.doctors.filter((d) => d !== name) : [...f.doctors, name],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patientName.trim() || !form.doctors.length) {
      setError('Patient name and at least one doctor are required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      let patientId = form.patientId;
      if (!patientId) {
        const pr = await ipApi.createPatient({
          name: form.patientName.trim(),
          phone: form.patientPhone,
          age: form.age,
          gender: form.gender,
          hospitalId: user.hospitalId,
        });
        patientId = pr?.patient?.patientId;
      }
      for (const doctor of form.doctors) {
        await ipApi.addToQueue({
          ...form,
          doctor,
          patientId,
          hospitalId: user.hospitalId,
          visitDate: form.visitDate || new Date().toISOString().slice(0, 10),
          status: Q.BOOKED,
          service: 'Consultation',
          paymentMode: 'Cash',
        });
      }
      onDone?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card mb q-book-form" onSubmit={submit}>
      <h3 style={{ margin: '0 0 12px', color: '#0d6e6a' }}>
        {defaultVisitType === 'Follow-up' ? 'Book follow-up' : 'Book appointment'}
      </h3>
      {error && <div className="error mb">{error}</div>}
      <div className="form-grid">
        <div className="field full">
          <label>Patient</label>
          <select className="input" value={patientPick} onChange={(e) => { setPatientPick(e.target.value); pickPatient(e.target.value); }}>
            <option value="">— Select or enter new —</option>
            {(patients || []).map((p) => (
              <option key={p.patientId} value={p.patientId}>{p.name} · {p.patientId}</option>
            ))}
          </select>
        </div>
        <div className="field"><label>Name *</label><input className="input" required value={form.patientName} onChange={(e) => setForm({ ...form, patientName: e.target.value })} /></div>
        <div className="field"><label>Mobile</label><input className="input" value={form.patientPhone} onChange={(e) => setForm({ ...form, patientPhone: e.target.value })} /></div>
        <div className="field full">
          <label>Doctors * (select one or more)</label>
          <div className="q-tag-row">
            {doctors.map((d) => (
              <button key={d.staffId} type="button" className={`q-tag${form.doctors.includes(d.name) ? ' on' : ''}`} onClick={() => toggleDoctor(d.name)}>
                {d.name}
              </button>
            ))}
          </div>
        </div>
        <div className="field"><label>Date</label><input className="input" type="date" value={form.visitDate} onChange={(e) => setForm({ ...form, visitDate: e.target.value })} /></div>
        <div className="field">
          <label>Slot</label>
          <select className="input" value={form.appointmentTime} onChange={(e) => setForm({ ...form, appointmentTime: e.target.value, time: e.target.value })}>
            {TIME_SLOTS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Duration (min)</label>
          <select className="input" value={form.slotDuration} onChange={(e) => setForm({ ...form, slotDuration: Number(e.target.value) })}>
            {[10, 15, 20, 30, 45, 60].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="field" style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.teleconsult} onChange={(e) => setForm({ ...form, teleconsult: e.target.checked })} />
            Teleconsult
          </label>
        </div>
        <div className="field full"><label>Reason</label><input className="input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
        <div className="field full">
          <label>Tags</label>
          <div className="q-tag-row">
            {DEFAULT_QUEUE_TAGS.map((t) => (
              <button key={t} type="button" className={`q-tag${form.tags.includes(t) ? ' on' : ''}`} onClick={() => toggleTag(t)}>{t}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="form-actions" style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Booking…' : 'Book appointment'}</button>
      </div>
    </form>
  );
}

export function QueueRowActions({
  row, busy, hospital, user, onPatch, onCollectFee, onReload, onPrintToken, onPrintAppt, onDelete,
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [reschedOpen, setReschedOpen] = useState(false);
  const [resched, setResched] = useState({
    visitDate: row.visitDate || new Date().toISOString().slice(0, 10),
    appointmentTime: row.appointmentTime || row.time || '09:00',
  });
  const [vitals, setVitals] = useState(row.preVitals || { bp: '', pulse: '', temp: '', spo2: '', weight: '' });
  const id = queueRowId(row);
  const canAdmit = ['reception', 'doctor', 'manager', 'ipd', 'nurse'].includes(user?.role);
  const admitHref = buildIpdAdmitLink({
    patientName: row.patientName || row.name,
    patientId: row.patientId,
    queueId: id,
    doctor: row.doctor,
    reason: row.reason,
    phone: row.patientPhone,
    visitType: row.visitType,
  });

  const goAdmit = () => {
    setOpen(false);
    navigate(admitHref);
  };

  const run = async (fn) => {
    setOpen(false);
    await fn();
  };

  const saveVitals = async () => {
    await onPatch(row, { preVitals: vitals });
    setVitalsOpen(false);
  };

  const sendSms = async () => {
    if (!row.patientPhone) return;
    try {
      await ipApi.sendSms({
        phone: row.patientPhone,
        message: `Dear ${row.name || row.patientName}, your token is #${row.token} with ${row.doctor} at ${hospital?.name || 'our hospital'}. — HCEN`,
        patientId: row.patientId,
        queueId: id,
      });
      onReload?.();
      window.alert('SMS sent to patient.');
    } catch (e) {
      window.alert(`SMS could not be sent: ${e.message || 'server error'}`);
    }
  };

  const shareTeleLink = async () => {
    if (!row.patientPhone) return;
    const link = row.teleLink || `${window.location.origin}/hms/telemedicine?patient=${encodeURIComponent(row.patientId || '')}&doctor=${encodeURIComponent(row.doctor || '')}`;
    try {
      await ipApi.sendSms({
        phone: row.patientPhone,
        message: `Dear ${row.name || row.patientName}, join your tele-consult with ${row.doctor}: ${link} — ${hospital?.name || 'HCEN'}`,
        patientId: row.patientId,
        queueId: id,
      });
      onReload?.();
      window.alert('Tele-consult link sent to patient.');
    } catch (e) {
      window.alert(`Link could not be sent: ${e.message || 'server error'}`);
    }
  };

  return (
    <div className="q-row-actions">
      {row.status === Q.BOOKED && (
        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => onPatch(row, { status: Q.CHECKED_IN })}>Check-in</button>
      )}
      {(row.status === Q.CHECKED_IN || row.status === Q.AWAITING_PAYMENT) && (
        <>
          <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => onPatch(row, { status: Q.WAITING_DOCTOR })}>Send to doctor</button>
          <button type="button" className="btn btn-sm btn-secondary" disabled={busy} onClick={() => onCollectFee(row)}>Bill</button>
        </>
      )}
      <div className="q-menu-wrap">
        <button type="button" className="btn btn-sm btn-secondary q-menu-btn" onClick={() => setOpen((o) => !o)} aria-label="More">⋮</button>
        {open && (
          <div className="q-menu">
            {row.status === Q.BOOKED && <button type="button" onClick={() => run(() => onPrintToken(row))}>Print token</button>}
            {(row.status === Q.BOOKED || row.visitDate) && onPrintAppt && (
              <button type="button" onClick={() => run(() => onPrintAppt(row))}>Print appointment slip</button>
            )}
            <button type="button" onClick={() => run(() => onPatch(row, { status: Q.WAITING_DOCTOR }))}>Send to doctor</button>
            <button type="button" onClick={() => run(() => onCollectFee(row))}>Create receipt</button>
            {row.patientPhone && <button type="button" onClick={() => run(sendSms)}>SMS token</button>}
            {row.teleconsult && row.patientPhone && <button type="button" onClick={() => run(shareTeleLink)}>Share tele link</button>}
            <button type="button" onClick={() => { setOpen(false); setVitalsOpen(true); }}>Pre-consult vitals</button>
            {canAdmit && row.status !== Q.CANCELLED && row.status !== Q.NO_SHOW && (
              <button type="button" onClick={goAdmit}>🏥 Admit to IPD</button>
            )}
            {row.status !== Q.COMPLETED && row.status !== Q.CANCELLED && (user?.role === 'manager' || user?.role === 'doctor') && (
              <button type="button" onClick={() => run(() => onPatch(row, { status: Q.COMPLETED }))}>End visit</button>
            )}
            {row.status === Q.BOOKED && (
              <>
                <button type="button" onClick={() => { setOpen(false); setReschedOpen(true); }}>Reschedule</button>
                <button type="button" onClick={() => run(async () => {
                  if (!window.confirm(`Mark ${row.name || row.patientName || 'this patient'} as No-show?`)) return;
                  await onPatch(row, { status: Q.NO_SHOW });
                })}>No-show</button>
                <button type="button" onClick={() => run(async () => {
                  if (!window.confirm(`Cancel appointment for ${row.name || row.patientName || 'this patient'}?`)) return;
                  await onPatch(row, { status: Q.CANCELLED });
                })}>Cancel</button>
                <button type="button" onClick={() => run(async () => {
                  if (!window.confirm(`Delete queue entry for ${row.name || row.patientName || 'this patient'}? This cannot be undone.`)) return;
                  if (onDelete) await onDelete(row); else await ipApi.deleteQueue(id);
                  onReload?.();
                })}>Delete</button>
              </>
            )}
          </div>
        )}
      </div>
      {reschedOpen && (
        <div className="q-modal-backdrop" onClick={() => setReschedOpen(false)}>
          <div className="q-modal card" onClick={(e) => e.stopPropagation()}>
            <h4>Reschedule — {row.name || row.patientName}</h4>
            <div className="form-grid">
              <div className="field"><label>Date</label><input className="input" type="date" value={resched.visitDate} onChange={(e) => setResched({ ...resched, visitDate: e.target.value })} /></div>
              <div className="field"><label>Time</label><input className="input" value={resched.appointmentTime} onChange={(e) => setResched({ ...resched, appointmentTime: e.target.value, time: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setReschedOpen(false)}>Close</button>
              <button type="button" className="btn btn-primary" onClick={async () => {
                await onPatch(row, { ...resched, status: Q.BOOKED });
                setReschedOpen(false);
              }}>Save</button>
            </div>
          </div>
        </div>
      )}
      {vitalsOpen && (
        <div className="q-modal-backdrop" onClick={() => setVitalsOpen(false)}>
          <div className="q-modal card" onClick={(e) => e.stopPropagation()}>
            <h4>Pre-consult vitals — {row.name || row.patientName}</h4>
            <div className="form-grid">
              <div className="field"><label>BP</label><input className="input" value={vitals.bp} onChange={(e) => setVitals({ ...vitals, bp: e.target.value })} placeholder="120/80" /></div>
              <div className="field"><label>Pulse</label><input className="input" value={vitals.pulse} onChange={(e) => setVitals({ ...vitals, pulse: e.target.value })} /></div>
              <div className="field"><label>Temp °F</label><input className="input" value={vitals.temp} onChange={(e) => setVitals({ ...vitals, temp: e.target.value })} /></div>
              <div className="field"><label>SpO₂ %</label><input className="input" value={vitals.spo2} onChange={(e) => setVitals({ ...vitals, spo2: e.target.value })} /></div>
              <div className="field"><label>Weight kg</label><input className="input" value={vitals.weight} onChange={(e) => setVitals({ ...vitals, weight: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setVitalsOpen(false)}>Close</button>
              <button type="button" className="btn btn-primary" onClick={saveVitals}>Save vitals</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
